import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
  Users,
  Plus,
  Edit,
  Trash2,
  Search,
  Shield,
  Mail,
  Eye,
  EyeOff,
  Radio,
  LayoutDashboard,
  KeyRound,
  ChevronDown,
  ChevronUp,
  Globe,
  Briefcase,
} from 'lucide-react';
import { adminAPI, announcementZonesAPI } from '../../services/api';
import toast from 'react-hot-toast';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import PageHeader from '../../components/UI/PageHeader';
import Card from '../../components/UI/Card';
import Button from '../../components/UI/Button';
import StatusBadge from '../../components/UI/StatusBadge';
import EmptyState from '../../components/UI/EmptyState';

// CR 9.1 — Admin assigns Operators, Supervisors, and Managers to zones so
// their gate visibility can be scoped.
const ZONE_ASSIGNABLE_ROLES = ['gate_operator', 'supervisor', 'manager'];

// select/select-bordered and checkbox/checkbox-primary were DaisyUI class
// names left over from before this app switched to hand-rolled Tailwind
// components (see index.css) — DaisyUI was never actually installed, so
// every <select>/checkbox on this page has been rendering completely
// unstyled (native browser chrome) rather than matching the rest of the
// app. Replacing with the same input-style classes .input itself applies.
const SELECT_CLASS = 'block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm';
const CHECKBOX_CLASS = 'h-4 w-4 rounded border-gray-300 accent-blue-600';

const AdminUsersPage = () => {
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // 'create' or 'edit'
  const [selectedUser, setSelectedUser] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [selectedZoneIds, setSelectedZoneIds] = useState([]);
  const [zoneSearch, setZoneSearch] = useState('');

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    newPassword: '',
    role: 'gate_operator',
    isActive: true
  });

  // Fetch all zones for the zone-operator assignment checklist
  const { data: allZonesData } = useQuery('all-zones-admin', () => announcementZonesAPI.getAll(), {
    select: (res) => res.data?.data || [],
    refetchOnWindowFocus: false,
  });

  // Reset to page 1 when filters change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [roleFilter, statusFilter, searchTerm]);

  // Fetch users
  const { data: usersData, isLoading, error, refetch } = useQuery(
    ['admin-users', searchTerm, roleFilter, statusFilter, currentPage, itemsPerPage],
    () => adminAPI.getAllUsers({
      search: searchTerm || undefined,
      role: roleFilter === 'all' ? undefined : roleFilter,
      isActive: statusFilter === 'all' ? undefined : statusFilter === 'active',
      page: currentPage,
      limit: itemsPerPage,
      sortBy: 'createdAt',
      sortOrder: 'DESC'
    }),
    {
      refetchOnWindowFocus: false,
      keepPreviousData: true,
      onError: (error) => {
        console.error('Fetch users error:', error);
        toast.error('Failed to fetch users');
      }
    }
  );

  const users = usersData?.data?.users || usersData?.data?.data?.users || [];
  const pagination = usersData?.data?.pagination || usersData?.data?.data?.pagination || null;

  // Create user mutation
  const createUserMutation = useMutation(
    (userData) => adminAPI.createUser(userData),
    {
      onSuccess: (response) => {
        const newUserId = response.data?.data?.user?.id;
        if (newUserId && ZONE_ASSIGNABLE_ROLES.includes(formData.role) && selectedZoneIds.length > 0) {
          updateZonesMutation.mutate({ id: newUserId, zone_ids: selectedZoneIds });
        }
        queryClient.invalidateQueries(['admin-users']);
        toast.success('User created successfully');
        handleCloseModal();
      },
      onError: (error) => {
        const message = error.response?.data?.message || 'Failed to create user';
        toast.error(message);
      }
    }
  );

  // Update user mutation (success/error handled inline in handleSubmit)
  const updateUserMutation = useMutation(
    ({ id, data }) => adminAPI.updateUser(id, data)
  );

  // Update zone assignments mutation
  const updateZonesMutation = useMutation(
    ({ id, zone_ids }) => adminAPI.setUserZones(id, zone_ids),
    {
      onError: () => toast.error('Failed to update zone assignments'),
    }
  );

  // Delete user mutation
  const deleteUserMutation = useMutation(
    (id) => adminAPI.deleteUser(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['admin-users']);
        toast.success('User deleted successfully');
        setShowDeleteModal(false);
        setSelectedUser(null);
      },
      onError: (error) => {
        const message = error.response?.data?.message || 'Failed to delete user';
        toast.error(message);
      }
    }
  );

  const handleOpenCreateModal = () => {
    setModalMode('create');
    setFormData({ name: '', email: '', password: '', newPassword: '', role: 'gate_operator', isActive: true });
    setSelectedZoneIds([]);
    setZoneSearch('');
    setShowModal(true);
    setShowPassword(false);
    setShowChangePassword(false);
    setShowNewPassword(false);
  };

  const handleOpenEditModal = (user) => {
    setModalMode('edit');
    setSelectedUser(user);
    setFormData({
      name: user.name,
      email: user.email,
      password: '',
      newPassword: '',
      role: user.role,
      isActive: user.isActive ?? user.is_active ?? true
    });
    const preAssignedZones = (user.assignedZones || []).map(z => z.id);
    setSelectedZoneIds(preAssignedZones);
    setZoneSearch('');
    setShowModal(true);
    setShowPassword(false);
    setShowChangePassword(false);
    setShowNewPassword(false);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setModalMode('create');
    setSelectedUser(null);
    setFormData({ name: '', email: '', password: '', newPassword: '', role: 'gate_operator', isActive: true });
    setSelectedZoneIds([]);
    setZoneSearch('');
    setShowPassword(false);
    setShowChangePassword(false);
    setShowNewPassword(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (modalMode === 'create') {
      if (!formData.name || !formData.email || !formData.password) {
        toast.error('Please fill in all required fields');
        return;
      }
      createUserMutation.mutate(formData);
    } else {
      if (!formData.name || !formData.email) {
        toast.error('Please fill in all required fields');
        return;
      }
      if (showChangePassword && formData.newPassword && formData.newPassword.length < 6) {
        toast.error('New password must be at least 6 characters');
        return;
      }
      const updateData = {
        name: formData.name,
        email: formData.email,
        role: formData.role,
        isActive: formData.isActive,
        ...(showChangePassword && formData.newPassword ? { password: formData.newPassword } : {})
      };
      try {
        await updateUserMutation.mutateAsync({ id: selectedUser.id, data: updateData });
        if (ZONE_ASSIGNABLE_ROLES.includes(formData.role)) {
          await updateZonesMutation.mutateAsync({ id: selectedUser.id, zone_ids: selectedZoneIds });
        }
        await queryClient.invalidateQueries(['admin-users']);
        toast.success('User updated successfully');
        handleCloseModal();
      } catch (error) {
        const message = error.response?.data?.message || 'Failed to update user';
        toast.error(message);
      }
    }
  };

  const handleDeleteClick = (user) => {
    setSelectedUser(user);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = () => {
    if (selectedUser) {
      deleteUserMutation.mutate(selectedUser.id);
    }
  };

  const ROLE_LABELS = {
    admin:         'Admin',
    gate_operator: 'Gate Operator',
    supervisor:    'Supervisor',
    manager:       'Manager',
  };

  const getRoleTone = (role) => {
    const tones = {
      admin:         'purple',
      gate_operator: 'success',
      supervisor:    'orange',
      manager:       'teal',
    };
    return tones[role] || 'neutral';
  };

  const getRoleIcon = (role) => {
    const icons = {
      admin:         Shield,
      gate_operator: Radio,
      supervisor:    LayoutDashboard,
      manager:       Briefcase,
    };
    return icons[role] || Users;
  };

  if (isLoading && !usersData) return <LoadingSpinner />;
  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">Failed to load users. Please try again.</p>
        <Button variant="primary" onClick={() => refetch()} className="mt-4">
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Users"
        subtitle={`Manage system users and their permissions (${pagination?.total || users.length} total).`}
        actions={
          <>
            <Button variant="ghost" size="sm" onClick={() => refetch()} disabled={isLoading}>
              {isLoading ? 'Loading...' : 'Refresh'}
            </Button>
            <Button variant="primary" icon={Plus} onClick={handleOpenCreateModal}>
              Add User
            </Button>
          </>
        }
      />

      {/* Filters */}
      <Card>
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Search */}
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search users by name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 input w-full"
              />
            </div>
          </div>

          {/* Role Filter */}
          <div className="sm:w-48">
            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              className={SELECT_CLASS}
            >
              <option value="all">All Roles</option>
              <option value="admin">Admin</option>
              <option value="manager">Manager</option>
              <option value="supervisor">Supervisor</option>
              <option value="gate_operator">Gate Operator</option>
            </select>
          </div>

          {/* Status Filter */}
          <div className="sm:w-48">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className={SELECT_CLASS}
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </div>
      </Card>

      {/* Users Table */}
      <Card>
          {users.length === 0 ? (
            <EmptyState
              icon={Users}
              title="No users found"
              subtitle={searchTerm || roleFilter !== 'all' || statusFilter !== 'all'
                ? 'Try adjusting your filters.'
                : 'Get started by creating a new user.'}
            />
          ) : (
            <div className="overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      User
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Role
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {users.map((user) => {
                    const RoleIcon = getRoleIcon(user.role);
                    const isActive = user.isActive ?? user.is_active ?? true;
                    
                    return (
                      <tr key={user.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0">
                              <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
                                <RoleIcon className="h-5 w-5 text-primary-600" />
                              </div>
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">
                                {user.name}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center text-sm text-gray-500">
                            <Mail className="h-4 w-4 mr-2 text-gray-400" />
                            {user.email}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex flex-col gap-1">
                            <StatusBadge label={ROLE_LABELS[user.role] || user.role} tone={getRoleTone(user.role)} className="w-fit" />
                            {ZONE_ASSIGNABLE_ROLES.includes(user.role) && user.assignedZones && user.assignedZones.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-0.5">
                                {user.assignedZones.map(z => (
                                  <span key={z.id} className="inline-flex px-1.5 py-0.5 text-xs rounded bg-gray-100 text-gray-600 font-mono">
                                    {z.zone_code}
                                  </span>
                                ))}
                              </div>
                            )}
                            {ZONE_ASSIGNABLE_ROLES.includes(user.role) && (!user.assignedZones || user.assignedZones.length === 0) && (
                              <span className="text-xs text-gray-400">All zones (unrestricted)</span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <StatusBadge label={isActive ? 'Active' : 'Inactive'} tone={isActive ? 'success' : 'danger'} />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex space-x-2">
                            <button
                              onClick={() => handleOpenEditModal(user)}
                              className="text-primary-600 hover:text-primary-900"
                              title="Edit User"
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteClick(user)}
                              className="text-red-600 hover:text-red-900"
                              title="Delete User"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {pagination && pagination.totalPages > 1 && (
            <div className="mt-6 flex items-center justify-between border-t border-gray-200 pt-4">
              {/* Pagination Info */}
              <div className="flex items-center gap-4">
                <p className="text-sm text-gray-700">
                  Showing <span className="font-medium">{((currentPage - 1) * itemsPerPage) + 1}</span> to{' '}
                  <span className="font-medium">
                    {Math.min(currentPage * itemsPerPage, pagination.total)}
                  </span> of{' '}
                  <span className="font-medium">{pagination.total}</span> results
                </p>

                {/* Items per page selector */}
                <select
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  className={`${SELECT_CLASS} w-auto`}
                >
                  <option value={10}>10 per page</option>
                  <option value={20}>20 per page</option>
                  <option value={50}>50 per page</option>
                  <option value={100}>100 per page</option>
                </select>
              </div>

              {/* Pagination Buttons */}
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" onClick={() => setCurrentPage(1)} disabled={currentPage === 1}>«</Button>
                <Button variant="ghost" size="sm" onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))} disabled={currentPage === 1}>‹</Button>

                {/* Page Numbers */}
                <div className="flex items-center gap-1">
                  {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                    let pageNum;
                    if (pagination.totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= pagination.totalPages - 2) {
                      pageNum = pagination.totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }

                    return (
                      <Button
                        key={pageNum}
                        variant={currentPage === pageNum ? 'primary' : 'ghost'}
                        size="sm"
                        onClick={() => setCurrentPage(pageNum)}
                      >
                        {pageNum}
                      </Button>
                    );
                  })}
                </div>

                <Button variant="ghost" size="sm" onClick={() => setCurrentPage(prev => Math.min(pagination.totalPages, prev + 1))} disabled={currentPage === pagination.totalPages}>›</Button>
                <Button variant="ghost" size="sm" onClick={() => setCurrentPage(pagination.totalPages)} disabled={currentPage === pagination.totalPages}>»</Button>
              </div>
            </div>
          )}
      </Card>

      {/* Create/Edit User Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-md shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {modalMode === 'create' ? 'Create New User' : 'Edit User'}
              </h3>
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="input"
                    placeholder="Enter user name"
                    required
                  />
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="input"
                    placeholder="user@example.com"
                    required
                  />
                </div>

                {/* Password (only for create or if changing) */}
                {modalMode === 'create' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Password <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        className="input pr-10"
                        placeholder="Enter password"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                )}

                {/* Role */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Role <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.role}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                    className={SELECT_CLASS}
                  >
                    <option value="gate_operator">Gate Operator</option>
                    <option value="supervisor">Supervisor</option>
                    <option value="manager">Manager</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>

                {/* Zone Assignment — operator, supervisor, manager (CR 9.1) */}
                {ZONE_ASSIGNABLE_ROLES.includes(formData.role) && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <Globe className="inline h-4 w-4 mr-1 text-gray-500" />
                      Assigned Zones
                      <span className="text-xs text-gray-400 font-normal ml-1">(leave empty to see all gates)</span>
                    </label>
                    {!allZonesData || allZonesData.length === 0 ? (
                      <p className="text-sm text-gray-500">No active zones available.</p>
                    ) : (
                      <div className="border border-gray-200 rounded-lg overflow-hidden">
                        {/* Search input */}
                        <div className="px-3 py-2 border-b border-gray-200 bg-gray-50">
                          <div className="relative">
                            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                            <input
                              type="text"
                              placeholder="Search zones..."
                              value={zoneSearch}
                              onChange={(e) => setZoneSearch(e.target.value)}
                              className="w-full pl-7 pr-3 py-1 text-sm border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-primary-500 bg-white"
                            />
                          </div>
                        </div>
                        {/* Zone list */}
                        <div className="max-h-40 overflow-y-auto divide-y divide-gray-100">
                          {allZonesData
                            .filter(zone =>
                              !zoneSearch ||
                              zone.zone_code.toLowerCase().includes(zoneSearch.toLowerCase()) ||
                              (zone.zone_name || '').toLowerCase().includes(zoneSearch.toLowerCase()) ||
                              (zone.terminal || '').toLowerCase().includes(zoneSearch.toLowerCase())
                            )
                            .map((zone) => (
                              <label
                                key={zone.id}
                                className="flex items-center gap-3 px-3 py-2 hover:bg-gray-50 cursor-pointer"
                              >
                                <input
                                  type="checkbox"
                                  className={CHECKBOX_CLASS}
                                  checked={selectedZoneIds.includes(zone.id)}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setSelectedZoneIds(prev => [...prev, zone.id]);
                                    } else {
                                      setSelectedZoneIds(prev => prev.filter(id => id !== zone.id));
                                    }
                                  }}
                                />
                                <span className="text-sm text-gray-800 font-medium">{zone.zone_code}</span>
                                <span className="text-xs text-gray-500 truncate">{zone.zone_name}</span>
                              </label>
                            ))
                          }
                          {allZonesData.filter(zone =>
                            !zoneSearch ||
                            zone.zone_code.toLowerCase().includes(zoneSearch.toLowerCase()) ||
                            (zone.zone_name || '').toLowerCase().includes(zoneSearch.toLowerCase()) ||
                            (zone.terminal || '').toLowerCase().includes(zoneSearch.toLowerCase())
                          ).length === 0 && (
                            <p className="px-3 py-3 text-sm text-gray-400 text-center">No zones match "{zoneSearch}"</p>
                          )}
                        </div>
                      </div>
                    )}
                    <p className="mt-1 text-xs text-gray-400">
                      {selectedZoneIds.length === 0
                        ? 'No zones assigned — sees all zones (unrestricted).'
                        : `${selectedZoneIds.length} zone${selectedZoneIds.length > 1 ? 's' : ''} selected`}
                    </p>
                  </div>
                )}

                {/* Status */}
                <div>
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.isActive}
                      onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                      className={CHECKBOX_CLASS}
                    />
                    <span className="text-sm font-medium text-gray-700">Active User</span>
                  </label>
                </div>

                {/* Change Password — edit mode only */}
                {modalMode === 'edit' && (
                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <button
                      type="button"
                      onClick={() => { setShowChangePassword(s => !s); setFormData(f => ({ ...f, newPassword: '' })); }}
                      className="w-full flex items-center justify-between px-4 py-2.5 bg-gray-50 hover:bg-gray-100 text-sm font-medium text-gray-700 transition-colors"
                    >
                      <span className="flex items-center gap-2">
                        <KeyRound className="h-4 w-4 text-gray-400" />
                        Change Password
                      </span>
                      {showChangePassword ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
                    </button>
                    {showChangePassword && (
                      <div className="px-4 py-3 bg-white">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          New Password <span className="text-red-500">*</span>
                          <span className="ml-1 text-xs text-gray-400 font-normal">(min 6 characters)</span>
                        </label>
                        <div className="relative">
                          <input
                            type={showNewPassword ? 'text' : 'password'}
                            value={formData.newPassword}
                            onChange={(e) => setFormData({ ...formData, newPassword: e.target.value })}
                            className="input pr-10"
                            placeholder="Enter new password"
                            autoComplete="new-password"
                          />
                          <button
                            type="button"
                            onClick={() => setShowNewPassword(s => !s)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                          >
                            {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Buttons */}
                <div className="flex justify-end space-x-3 pt-4">
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={handleCloseModal}
                    disabled={createUserMutation.isLoading || updateUserMutation.isLoading}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="primary"
                    loading={createUserMutation.isLoading || updateUserMutation.isLoading}
                  >
                    {createUserMutation.isLoading || updateUserMutation.isLoading
                      ? 'Saving...'
                      : modalMode === 'create'
                      ? 'Create User'
                      : 'Update User'}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedUser && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Delete User
              </h3>
              <p className="text-sm text-gray-500 mb-4">
                Are you sure you want to delete user <span className="font-medium text-gray-900">{selectedUser.name}</span>? 
                This action cannot be undone.
              </p>
              <div className="flex justify-end space-x-3">
                <Button
                  variant="ghost"
                  onClick={() => {
                    setShowDeleteModal(false);
                    setSelectedUser(null);
                  }}
                  disabled={deleteUserMutation.isLoading}
                >
                  Cancel
                </Button>
                <Button
                  variant="danger"
                  onClick={handleConfirmDelete}
                  loading={deleteUserMutation.isLoading}
                >
                  {deleteUserMutation.isLoading ? 'Deleting...' : 'Delete User'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUsersPage;